/**
 * Video File Service
 * Simula el manejo de archivos de video para el panel de administración
 * En producción, esto debería ser reemplazado por llamadas reales al backend
 * @author Ana Lizcano
 * @version 1.0.0
 */

class VideoFileService {
  constructor() {
    this.baseFragmentPath = '/video/fragments/';
    this.videoMetadataKey = 'video_metadata_storage';
  }

  /**
   * Verifica si un video existe en el servidor
   */
  async checkVideoExists(url) {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Obtiene la lista de videos existentes desde localStorage y archivos estáticos
   */
  async getExistingVideos() {
    const staticVideos = [
      {
        id: 'song1',
        name: 'No Diggity',
        title: 'No Diggity',
        filename: 'fragment_1.mp4',
        url: '/video/fragments/fragment_1.mp4',
        size: 'Unknown',
        duration: '20s',
        uploadDate: 'Pre-existing',
        isStatic: true
      },
      {
        id: 'song2',
        name: 'Lose Yourself',
        title: 'Lose Yourself',
        filename: 'fragment_2.mp4',
        url: '/video/fragments/fragment_2.mp4',
        size: 'Unknown',
        duration: '20s',
        uploadDate: 'Pre-existing',
        isStatic: true
      },
      {
        id: 'song3',
        name: 'My Sharona',
        title: 'My Sharona',
        filename: 'fragment_3.mp4',
        url: '/video/fragments/fragment_3.mp4',
        size: 'Unknown',
        duration: '20s',
        uploadDate: 'Pre-existing',
        isStatic: true
      },
      {
        id: 'song4',
        name: 'Titanium',
        title: 'Titanium',
        filename: 'fragment_4.mp4',
        url: '/video/fragments/fragment_4.mp4',
        size: 'Unknown',
        duration: '20s',
        uploadDate: 'Pre-existing',
        isStatic: true
      }
    ];

    // Verificar disponibilidad de videos estáticos
    const availableStaticVideos = [];
    for (const video of staticVideos) {
      const exists = await this.checkVideoExists(video.url);
      if (exists) {
        availableStaticVideos.push(video);
      }
    }

    // Obtener videos dinámicos desde localStorage
    const dynamicVideos = this.getDynamicVideos();
    
    return [...availableStaticVideos, ...dynamicVideos];
  }

  /**
   * Obtiene videos dinámicos desde localStorage
   */
  getDynamicVideos() {
    try {
      const metadata = localStorage.getItem(this.videoMetadataKey);
      return metadata ? JSON.parse(metadata) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Guarda metadata de videos dinámicos en localStorage
   */
  saveDynamicVideos(videos) {
    try {
      localStorage.setItem(this.videoMetadataKey, JSON.stringify(videos));
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Simula la subida de un video al directorio fragments
   * En producción, esto haría una llamada al backend
   */
  async uploadVideo(file, title, progressCallback) {
    return new Promise((resolve, reject) => {
      try {
        // Generar nombre de archivo único
        const timestamp = Date.now();
        const nextFragmentNumber = this.getNextFragmentNumber();
        const filename = `fragment_${nextFragmentNumber}_${timestamp}.mp4`;
        
        // Simular progreso de subida
        let progress = 0;
        const progressInterval = setInterval(() => {
          progress += Math.random() * 20;
          if (progress > 95) progress = 95;
          progressCallback && progressCallback(progress);
        }, 200);

        // Simular delay de subida
        setTimeout(() => {
          clearInterval(progressInterval);
          progressCallback && progressCallback(100);

          // Crear URL del archivo usando createObjectURL para desarrollo
          const blobUrl = URL.createObjectURL(file);
          
          // En producción, usar la URL del servidor
          const serverUrl = `${this.baseFragmentPath}${filename}`;

          const newVideo = {
            id: `song${nextFragmentNumber}`,
            title: title,
            filename: filename,
            url: blobUrl, // URL temporal para desarrollo
            serverUrl: serverUrl, // URL donde debería estar en producción
            size: this.formatFileSize(file.size),
            duration: '20s',
            uploadDate: new Date().toLocaleDateString(),
            isStatic: false,
            blobUrl: blobUrl // Mantener referencia para limpieza
          };

          // Guardar metadata
          const dynamicVideos = this.getDynamicVideos();
          dynamicVideos.push(newVideo);
          this.saveDynamicVideos(dynamicVideos);

          // En producción, aquí harías:
          // await this.saveVideoToServer(file, filename);

          resolve(newVideo);
        }, 2000);

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Obtiene el siguiente número de fragmento disponible
   */
  getNextFragmentNumber() {
    const dynamicVideos = this.getDynamicVideos();
    const allVideos = [...Array.from({length: 4}, (_, i) => i + 1), ...dynamicVideos.map(v => {
      const match = v.filename.match(/fragment_(\d+)/);
      return match ? parseInt(match[1]) : 0;
    })];
    
    return Math.max(...allVideos) + 1;
  }

  /**
   * Elimina un video
   */
  async deleteVideo(videoId) {
    try {
      const dynamicVideos = this.getDynamicVideos();
      const videoIndex = dynamicVideos.findIndex(v => v.id === videoId);
      
      if (videoIndex === -1) {
        throw new Error('Cannot delete static video');
      }

      const video = dynamicVideos[videoIndex];
      
      // Limpiar URLs temporales
      if (video.blobUrl && video.blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(video.blobUrl);
      }
      if (video.url && video.url.startsWith('blob:')) {
        URL.revokeObjectURL(video.url);
      }

      // Remover de la lista
      dynamicVideos.splice(videoIndex, 1);
      this.saveDynamicVideos(dynamicVideos);

      // En producción, aquí eliminarías el archivo del servidor:
      // await this.deleteVideoFromServer(video.filename);

      return true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Actualiza un video existente
   */
  async updateVideo(videoId, updates) {
    try {
      const dynamicVideos = this.getDynamicVideos();
      const videoIndex = dynamicVideos.findIndex(v => v.id === videoId);
      
      if (videoIndex === -1) {
        throw new Error('Cannot update static video or video not found');
      }

      dynamicVideos[videoIndex] = { ...dynamicVideos[videoIndex], ...updates };
      this.saveDynamicVideos(dynamicVideos);

      return dynamicVideos[videoIndex];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Formatea el tamaño de archivo
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Obtiene videos formateados para blockC
   */
  async getVideosForBlockC() {
    const videos = await this.getExistingVideos();
    return videos.map(video => ({
      id: video.id,
      videoUrl: video.url
    }));
  }

  /**
   * En producción, este método haría la subida real al servidor
   */
  async saveVideoToServer(file, filename) {
    const formData = new FormData();
    formData.append('video', file);
    formData.append('filename', filename);

    const response = await fetch('/api/videos/upload', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to upload video to server');
    }

    return await response.json();
  }

  /**
   * En producción, este método eliminaría el archivo del servidor
   */
  async deleteVideoFromServer(filename) {
    const response = await fetch(`/api/videos/${filename}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      throw new Error('Failed to delete video from server');
    }

    return await response.json();
  }
}

export default new VideoFileService();